import { Severity } from 'enums/Severity';
import { StorageKey } from 'enums/StorageKey';
import { Logger } from 'interfaces/Logger';
import { LogService } from 'services/LogService';

import { ServiceConfiguration } from '../ServiceConfiguration';

const consoleLogger: Logger = {
    info: (scope: string, timestamp: number, msg: string) =>
        // eslint-disable-next-line no-console
        console.info(
            `%c${scope} ${new Date(timestamp).toLocaleTimeString()}`,
            'color: white; background-color: #0D69F2; padding: 2px 6px; border-radius: 2px;',
            msg,
        ),
    warn: (scope: string, timestamp: number, msg: string) =>
        // eslint-disable-next-line no-console
        console.warn(
            `%c${scope} ${new Date(timestamp).toLocaleTimeString()}`,
            'color: white; background-color: #FF8C19; padding: 2px 6px; border-radius: 2px;',
            msg,
        ),
    error: (scope: string, timestamp: number, msg: string) =>
        // eslint-disable-next-line no-console
        console.error(
            `%c${scope} ${new Date(timestamp).toLocaleTimeString()}`,
            'color: white; background-color: #DA0B20; padding: 2px 6px; border-radius: 2px;',
            msg,
        ),
};

export class DivLogger implements Logger {
    consoleElement: HTMLElement;

    constructor() {}

    setConsoleElement(consoleElement) {
        this.consoleElement = consoleElement;
    }

    private logToElement(scope: string, timestamp: number, msg: string, style: string): void {
        const message = document.createElement('div');
        message.innerHTML = `
            <div style="display: flex; background: white; flex-direction: column;">
                <div
                    style="${style}"
                >
                    ${scope} ${new Date(timestamp).toLocaleTimeString()}
                </div>
                <div
                    style="
                        text-overflow: clip;
                        text-wrap: balance;
                        word-wrap: break-word;
                    "
                >
                    ${msg}
                </div>
            </div>
        `;
        this.consoleElement.appendChild(message);
    }

    info(scope: string, timestamp: number, msg: string): void {
        this.logToElement(
            scope,
            timestamp,
            msg,
            'color: white; background-color: #0D69F2; padding: 2px 6px; border-radius: 2px;',
        );
    }

    warn(scope: string, timestamp: number, msg: string): void {
        this.logToElement(
            scope,
            timestamp,
            msg,
            'color: white; background-color: #FF8C19; padding: 2px 6px; border-radius: 2px;',
        );
    }

    error(scope: string, timestamp: number, msg: string): void {
        this.logToElement(
            scope,
            timestamp,
            msg,
            'color: white; background-color: #DA0B20; padding: 2px 6px; border-radius: 2px;',
        );
    }
}

export class ClipboardLogger implements Logger {
    buffer: string[] = [];

    private logToBuffer(scope: string, timestamp: number, msg: string, type: string) {
        if (this.buffer.length > 500) this.buffer.shift();
        this.buffer.push(`${type} ${scope} ${new Date(timestamp).toLocaleTimeString()} ${msg}`);
    }

    getToClipboard() {
        navigator.clipboard.writeText(this.buffer.join('\n'));
    }

    info(scope: string, timestamp: number, msg: string): void {
        this.logToBuffer(scope, timestamp, msg, 'INFO');
    }

    warn(scope: string, timestamp: number, msg: string): void {
        this.logToBuffer(scope, timestamp, msg, 'WARN');
    }

    error(scope: string, timestamp: number, msg: string): void {
        this.logToBuffer(scope, timestamp, msg, 'ERROR');
    }
}

export class KeyAstromLogger implements Logger {
    info(): void {}
    warn(): void {}
    error(): void {}
}

export class YandexMLogger implements Logger {
    info(): void {}
    warn(): void {}
    error(): void {}
}

export enum ELoggerKey {
    CLIPBOARD = 'clipboard',
    DIV = 'div',
    CONSOLE = 'console',
    KEYASTROM = 'keyAstrom',
    YANDEX = 'yandex',
}

export class LogServiceImpl implements LogService {
    private name = 'Widget';
    loggerEnablesMap_ = new Map<ELoggerKey, boolean>();
    loggerEnablesMap: typeof this.loggerEnablesMap_;
    hasLoggerEnablesMapChanged = true;
    loggerMap = new Map<ELoggerKey, Logger>();
    prevLoggerList: Logger[] = [];

    configure({ widgetParameters }: ServiceConfiguration): void {
        const { title, debugLevel, overrideFeatures } = widgetParameters;
        this.setDebugLevel(debugLevel);
        this.name = title;

        /* eslint-disable @typescript-eslint/no-this-alias */
        const self = this;

        this.loggerMap.set(ELoggerKey.CLIPBOARD, new ClipboardLogger());
        this.loggerMap.set(ELoggerKey.CONSOLE, consoleLogger);
        this.loggerMap.set(ELoggerKey.DIV, new DivLogger());
        this.loggerMap.set(ELoggerKey.KEYASTROM, new KeyAstromLogger());
        this.loggerMap.set(ELoggerKey.YANDEX, new YandexMLogger());

        this.loggerEnablesMap_.set(ELoggerKey.CLIPBOARD, !!overrideFeatures?.LogToClipboard);
        this.loggerEnablesMap_.set(ELoggerKey.CONSOLE, true);
        this.loggerEnablesMap_.set(ELoggerKey.DIV, !!overrideFeatures?.LogToDiv);
        this.loggerEnablesMap_.set(ELoggerKey.KEYASTROM, false);
        this.loggerEnablesMap_.set(ELoggerKey.YANDEX, false);

        this.loggerEnablesMap = new Proxy(this.loggerEnablesMap_, {
            get(target, prop) {
                if (prop === 'set') {
                    return function (key, value) {
                        self.hasLoggerEnablesMapChanged = true;
                        return target.set(key, value);
                    };
                }
                if (prop === 'get') {
                    return function (key) {
                        return target.get(key);
                    };
                }
            },
        });
    }

    private get loggerList() {
        if (this.hasLoggerEnablesMapChanged) {
            this.hasLoggerEnablesMapChanged = false;
            this.prevLoggerList = Object.values(ELoggerKey)
                .filter((key) => this.loggerEnablesMap_.get(key))
                .map((key) => this.loggerMap.get(key));
        }

        return this.prevLoggerList;
    }

    get clipboardLogger() {
        return this.loggerMap.get(ELoggerKey.CLIPBOARD) as ClipboardLogger;
    }

    get divLogger() {
        return this.loggerMap.get(ELoggerKey.DIV) as DivLogger;
    }

    private setDebugLevel(debugLevel: number) {
        window.__widgetDebugLevel = debugLevel;
        localStorage.setItem(StorageKey.DebugLevel, String(debugLevel));
    }

    public info(msg: string, severity = Severity.Low) {
        if ((window.__widgetDebugLevel ?? 0) < severity) return;

        this.loggerList.map((logger) => logger?.info(this.name, Date.now(), msg));
    }

    public warn(msg: string, severity = Severity.Medium) {
        if ((window.__widgetDebugLevel ?? 0) < severity) return;

        this.loggerList.map((logger) => logger?.warn(this.name, Date.now(), msg));
    }

    public error(msg: string, severity = Severity.High) {
        if ((window.__widgetDebugLevel ?? 0) < severity) return;

        this.loggerList.map((logger) => logger?.error(this.name, Date.now(), msg));
    }
}
